import { selectActiveTicket, selectCustomers, updateTicket } from '@/redux/data/dataSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { CloseOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, Select, Space, Typography } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const CustomerSelector = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [pendingCustomerChange, setPendingCustomerChange] = useState<string | null>(null);
  const [isClearingCustomer, setIsClearingCustomer] = useState(false);

  const activeTicket = useAppSelector(selectActiveTicket);
  const customers = useAppSelector(selectCustomers);

  const selectedCustomer = useMemo(
    () => customers?.find((c) => c._id === activeTicket?.customerId),
    [customers, activeTicket?.customerId]
  );

  const hasItemsInTicket = useMemo(() => {
    return activeTicket?.items && activeTicket.items.length > 0;
  }, [activeTicket?.items]);

  const handleCustomerChange = useCallback(
    (customerId: string | null) => {
      // If there are items in the ticket, show warning modal
      if (hasItemsInTicket && customerId !== activeTicket?.customerId) {
        setPendingCustomerChange(customerId);
        setIsClearingCustomer(customerId === null);
        return;
      }

      // No items or same customer, proceed normally
      applyCustomerChange(customerId);
    },
    [activeTicket?.customerId, hasItemsInTicket]
  );

  const applyCustomerChange = useCallback(
    (customerId: string | null) => {
      if (activeTicket) {
        const newTicket = structuredClone(activeTicket);
        newTicket.customerId = customerId || undefined;
        dispatch(updateTicket(newTicket));
      }
      setPendingCustomerChange(null);
      setIsClearingCustomer(false);
    },
    [activeTicket, dispatch]
  );

  const handleClearButton = useCallback(() => {
    // Only show warning modal if there are items in the ticket
    if (hasItemsInTicket) {
      setPendingCustomerChange(null);
      setIsClearingCustomer(true);
    } else {
      // No items, directly clear the customer
      applyCustomerChange(null);
    }
  }, [hasItemsInTicket, applyCustomerChange]);

  const handleClearTicket = useCallback(() => {
    if (activeTicket) {
      const newTicket = structuredClone(activeTicket);
      newTicket.items = [];
      newTicket.discounts = [];
      newTicket.customerId = pendingCustomerChange || undefined;
      dispatch(updateTicket(newTicket));
    }
    setPendingCustomerChange(null);
    setIsClearingCustomer(false);
  }, [activeTicket, pendingCustomerChange, dispatch]);

  const handleCancelChange = useCallback(() => {
    setPendingCustomerChange(null);
    setIsClearingCustomer(false);
  }, []);

  const getNewCustomerName = useCallback(() => {
    if (isClearingCustomer) {
      return t('common:no_customer');
    }
    if (!pendingCustomerChange) return '';
    return customers?.find((c) => c._id === pendingCustomerChange)?.name || '';
  }, [customers, pendingCustomerChange, isClearingCustomer, t]);

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <Space direction="vertical" className="w-full">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserOutlined className="text-primary" />
            <Text strong className="text-gray-700 dark:text-gray-200">
              {t('common:customer_information')}
            </Text>
          </div>
          {selectedCustomer && (
            <Button type="text" danger onClick={handleClearButton} icon={<CloseOutlined />}>
              {t('common:clear')}
            </Button>
          )}
        </div>
        <Select
          showSearch
          allowClear
          placeholder={t('common:select_customer')}
          className="w-full"
          onChange={handleCustomerChange}
          value={activeTicket?.customerId}
          optionFilterProp="label"
          status={!activeTicket?.customerId ? 'warning' : undefined}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={customers?.map((customer) => ({
            value: customer._id,
            label: customer.name,
            description: customer.phone,
          }))}
          optionRender={(option) => (
            <Space>
              <Text>{option.data.label}</Text>
              <Text type="secondary" className="text-xs">
                ({option.data.description})
              </Text>
            </Space>
          )}
        />
        {!activeTicket?.customerId && (
          <Alert
            message={t('common:please_select_customer')}
            type="warning"
            showIcon
            className="mt-2"
          />
        )}
        {selectedCustomer && (
          <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <UserOutlined className="text-primary text-lg" />
              </div>
              <div>
                <Text strong className="block text-gray-800 dark:text-gray-100">
                  {selectedCustomer.name}
                </Text>
                <Text type="secondary" className="block text-sm">
                  {selectedCustomer.phone}
                </Text>
                {selectedCustomer.email && (
                  <Text type="secondary" className="block text-sm">
                    {selectedCustomer.email}
                  </Text>
                )}
              </div>
            </div>
          </div>
        )}
      </Space>

      {/* Customer Change Warning Modal */}
      <Modal
        open={pendingCustomerChange !== null || isClearingCustomer}
        title={t('common:customer_change_warning_title')}
        onCancel={handleCancelChange}
        footer={[
          <Button key="cancel" onClick={handleCancelChange}>
            {t('common:cancel')}
          </Button>,
          <Button key="clear" type="primary" danger onClick={handleClearTicket}>
            {t('common:clear_ticket_and_continue')}
          </Button>,
        ]}
      >
        <div className="space-y-3">
          <Text>
            {t('common:customer_change_warning_message', {
              customerName: getNewCustomerName(),
            })}
          </Text>
          <Alert message={t('common:customer_change_warning_note')} type="warning" showIcon />
        </div>
      </Modal>
    </div>
  );
};

export default CustomerSelector;
